import { SEO_META, TOOL_SEO_META, JSON_LD_SCHEMA, SITE_CONFIG } from '../config/seo.config';

/**
 * 更新頁面 Meta 標籤
 * @param {string} lang - 語言代碼
 * @param {string} toolId - 工具 ID (可選)
 */
export const updateMetaTags = (lang = 'zh', toolId = null) => {
  const langSeo = SEO_META[lang] || SEO_META.zh;
  const toolSeo = toolId && TOOL_SEO_META[toolId]?.[lang];
  const seo = toolSeo ? { ...langSeo, ...toolSeo } : langSeo;

  // 更新 HTML lang 屬性
  document.documentElement.lang = seo.lang;

  // 更新 title
  document.title = seo.title;

  // 構建分享 URL
  const shareUrl = getCleanUrl(lang, toolId);

  // 更新或創建 meta 標籤的輔助函數
  const setMeta = (selector, attribute, value) => {
    let el = document.querySelector(selector);
    if (!el) {
      el = document.createElement('meta');
      const isProperty = selector.includes('property=');
      if (isProperty) {
        el.setAttribute('property', selector.match(/property="([^"]+)"/)[1]);
      } else {
        el.setAttribute('name', selector.match(/name="([^"]+)"/)[1]);
      }
      document.head.appendChild(el);
    }
    el.setAttribute(attribute, value);
  };

  // 基本 Meta 標籤
  setMeta('meta[name="description"]', 'content', seo.description);
  setMeta('meta[name="keywords"]', 'content', seo.keywords);
  setMeta('meta[name="language"]', 'content', seo.lang);

  // Open Graph
  setMeta('meta[property="og:title"]', 'content', seo.ogTitle || seo.title);
  setMeta('meta[property="og:description"]', 'content', seo.ogDescription || seo.description);
  setMeta('meta[property="og:url"]', 'content', shareUrl);
  setMeta('meta[property="og:image"]', 'content', SITE_CONFIG.ogImage);
  setMeta('meta[property="og:locale"]', 'content', seo.locale);
  setMeta('meta[property="og:type"]', 'content', 'website');
  setMeta('meta[property="og:site_name"]', 'content', 'Convert Master');

  // Twitter Card
  setMeta('meta[name="twitter:card"]', 'content', 'summary_large_image');
  setMeta('meta[name="twitter:title"]', 'content', seo.ogTitle || seo.title);
  setMeta('meta[name="twitter:description"]', 'content', seo.ogDescription || seo.description);
  setMeta('meta[name="twitter:image"]', 'content', SITE_CONFIG.twitterImage);
  setMeta('meta[name="twitter:url"]', 'content', shareUrl);

  // Canonical URL
  let canonical = document.querySelector('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    document.head.appendChild(canonical);
  }
  canonical.setAttribute('href', shareUrl);

  // 更新結構化數據
  updateStructuredData(lang, toolId);
};

/**
 * 獲取乾淨 URL（不含查詢參數）
 */
const getCleanUrl = (lang, toolId = null) => {
  const base = lang === 'zh'
    ? SITE_CONFIG.url
    : `${SITE_CONFIG.url}/${lang}`;
  return toolId ? `${base}/#/tool/${toolId}` : `${base}/`;
};

/**
 * 更新結構化數據(JSON-LD)
 */
export const updateStructuredData = (lang = 'zh', toolId = null) => {
  // 移除舊的結構化數據（保留 index.html 中的靜態 schema）
  const dynamicScripts = document.querySelectorAll('script[data-seo-dynamic]');
  dynamicScripts.forEach(script => script.remove());

  const langSeo = SEO_META[lang] || SEO_META.zh;
  const toolSeo = toolId && TOOL_SEO_META[toolId]?.[lang];
  const seo = toolSeo ? { ...langSeo, ...toolSeo } : langSeo;

  const websiteSchema = {
    ...JSON_LD_SCHEMA.website,
    name: seo.ogTitle || 'Convert Master',
    description: seo.ogDescription || seo.description,
    inLanguage: seo.lang,
    url: getCleanUrl(lang, toolId),
  };

  addDynamicStructuredData(websiteSchema);

  if (toolId) {
    const toolNames = {
      'pdf-to-image':  { zh: 'PDF轉圖片', en: 'PDF to Image', ja: 'PDF画像変換', es: 'PDF a Imagen' },
      'pdf-compress':  { zh: 'PDF壓縮', en: 'PDF Compression', ja: 'PDF圧縮', es: 'Compresión PDF' },
      'image-convert': { zh: '圖片格式轉換', en: 'Image Format Conversion', ja: '画像形式変換', es: 'Conversión de Imagen' },
      'image-resize':  { zh: '圖片調整大小', en: 'Image Resize', ja: '画像サイズ変更', es: 'Redimensionar Imagen' },
      'image-compress':{ zh: '圖片壓縮', en: 'Image Compression', ja: '画像圧縮', es: 'Compresión de Imagen' },
    };
    const toolName = toolNames[toolId]?.[lang] || toolNames[toolId]?.zh || 'Tool';
    addDynamicStructuredData(JSON_LD_SCHEMA.breadcrumb(toolId, toolName));
  }
};

const addDynamicStructuredData = (schema) => {
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.setAttribute('data-seo-dynamic', 'true');
  script.textContent = JSON.stringify(schema);
  document.head.appendChild(script);
};

/**
 * 生成分享資訊
 */
export const getShareInfo = (lang = 'zh', toolId = null) => {
  const langSeo = SEO_META[lang] || SEO_META.zh;
  const toolSeo = toolId && TOOL_SEO_META[toolId]?.[lang];
  const seo = toolSeo ? { ...langSeo, ...toolSeo } : langSeo;

  return {
    title: seo.ogTitle || seo.title,
    description: seo.ogDescription || seo.description,
    url: getCleanUrl(lang, toolId),
    image: SITE_CONFIG.ogImage,
    hashtags: ['ConvertMaster', 'FileConversion', 'Privacy'],
  };
};

/**
 * 生成社交媒體分享連結
 */
export const generateShareLink = (platform, lang = 'zh', toolId = null) => {
  const shareInfo = getShareInfo(lang, toolId);
  const encodedUrl = encodeURIComponent(shareInfo.url);
  const encodedTitle = encodeURIComponent(shareInfo.title);

  switch (platform) {
    case 'facebook':
      return `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
    case 'twitter':
      return `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}&hashtags=${shareInfo.hashtags.join(',')}`;
    case 'linkedin':
      return `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;
    case 'whatsapp':
      return `https://wa.me/?text=${encodedTitle}%20${encodedUrl}`;
    case 'telegram':
      return `https://t.me/share/url?url=${encodedUrl}&text=${encodedTitle}`;
    default:
      return shareInfo.url;
  }
};

export const generateDynamicSitemap = () => {
  const tools = ['pdf-to-image', 'pdf-compress', 'image-convert', 'image-resize', 'image-compress'];
  const languages = ['zh', 'en', 'ja', 'es'];
  const today = new Date().toISOString().split('T')[0];
  const getUrl = (lang) => lang === 'zh' ? `${SITE_CONFIG.url}/` : `${SITE_CONFIG.url}/${lang}/`;

  let sitemap = `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9"
        xmlns:xhtml="http://www.w3.org/1999/xhtml">
  <url>
    <loc>${SITE_CONFIG.url}/</loc>
    <lastmod>${today}</lastmod>
    <changefreq>weekly</changefreq>
    <priority>1.0</priority>`;

  languages.forEach(lang => {
    sitemap += `
    <xhtml:link rel="alternate" hreflang="${lang === 'zh' ? 'zh-TW' : lang}" href="${getUrl(lang)}"/>`;
  });
  sitemap += `
    <xhtml:link rel="alternate" hreflang="x-default" href="${SITE_CONFIG.url}/"/>
  </url>`;

  languages.filter(l => l !== 'zh').forEach(lang => {
    sitemap += `
  <url>
    <loc>${getUrl(lang)}</loc>
    <lastmod>${today}</lastmod>
    <changefreq>weekly</changefreq>
    <priority>0.9</priority>
  </url>`;
  });

  tools.forEach(toolId => {
    languages.forEach(lang => {
      const pageUrl = lang === 'zh'
        ? `${SITE_CONFIG.url}/#/tool/${toolId}`
        : `${SITE_CONFIG.url}/${lang}/#/tool/${toolId}`;
      sitemap += `
  <url>
    <loc>${pageUrl}</loc>
    <lastmod>${today}</lastmod>
    <changefreq>monthly</changefreq>
    <priority>0.8</priority>`;
      languages.forEach(altLang => {
        const altUrl = altLang === 'zh'
          ? `${SITE_CONFIG.url}/#/tool/${toolId}`
          : `${SITE_CONFIG.url}/${altLang}/#/tool/${toolId}`;
        sitemap += `
    <xhtml:link rel="alternate" hreflang="${altLang === 'zh' ? 'zh-TW' : altLang}" href="${altUrl}"/>`;
      });
      sitemap += `
  </url>`;
    });
  });

  sitemap += `
</urlset>`;
  return sitemap;
};

export const submitSitemapToSearchEngines = () => {
  const sitemapUrl = encodeURIComponent(`${SITE_CONFIG.url}/sitemap.xml`);
  console.log('Google ping:', `https://www.google.com/ping?sitemap=${sitemapUrl}`);
  console.log('Bing ping:', `https://www.bing.com/ping?sitemap=${sitemapUrl}`);
};